import React from 'react';
import type { ResourceType } from '@cotera/api';
import { Assert } from '@cotera/utilities';
import { useResourceAccess } from '@cotera/client/app/v1/watchables/use-resource-access';
import { UnsharedArtifactNotice } from './unshared-artifact-notice';
import { ArchivedArtifactNotice } from './archived-artifact-notice';

/**
 * Probes the resource backing a chat artifact before mounting its (heavy) page
 * component, and stands in for the page when the answer is that there is
 * nothing to show.
 *
 * Two answers reach that: `denied` — the chat was shared but this asset was
 * not — and `archived`, where the resource is perfectly readable and simply
 * isn't meant to be on screen any more. Probing first also avoids the folder
 * artifact view's throwing suspense query, which otherwise hangs the panel.
 *
 * Archived is checked here rather than inside each resource page because the
 * pages disagree about it: the workflow and chat editors draw an
 * `ArchivedNotice` band and carry on, while the dataset and document pages have
 * no notion of it at all and render as though nothing had happened. In the
 * panel that produced the blank Canvas this gate exists to replace. One verdict
 * in front of every renderer is what makes the state look the same whichever
 * artifact you archived.
 *
 * The verdict comes from a `ResourceUpdated` watchable, so it stays live: a
 * workflow the chat is still creating, or a resource shared with the viewer
 * while the panel is open, resolves on the event instead of holding the
 * first answer until a reload — and an artifact archived from its own `⋯` menu
 * flips to the notice under the user's hand rather than on the next reload.
 */
export const ChatArtifactAccessGate: React.FC<{
  type: ResourceType;
  id: string;
  artifactType: string;
  title?: string;
  fallback: React.ReactNode;
  children: React.ReactNode;
}> = ({ type, id, artifactType, title, fallback, children }) => {
  const access = useResourceAccess(type, id);

  switch (access) {
    case 'loading':
      return <>{fallback}</>;
    case 'denied':
      return (
        <UnsharedArtifactNotice artifactType={artifactType} title={title} />
      );
    case 'archived':
      return (
        <ArchivedArtifactNotice artifactType={artifactType} title={title} />
      );
    case 'accessible':
      return <>{children}</>;
    default:
      return Assert.unreachable(access);
  }
};
